import { NavLink, useLocation } from 'react-router';
import {
  Activity,
  Bug,
  LifeBuoy,
  LogOut,
  Moon,
  Package,
  Rocket,
  Settings,
  Sun,
  Terminal,
  Ticket,
} from 'lucide-react';
import { ROUTES } from '@/constants';
import { useTheme } from '@/providers';
import { useLogout } from '@/services/queries';
import { useAppSelector } from '@/store/hooks';
import { AppShell, navItemClass, type NavItem } from '@/shared/ui/app-shell';
import { Logo } from '@/shared/ui/logo';

/**
 * The operations portal's shell.
 *
 * All the layout lives in the shared AppShell; this supplies what is specific
 * to this portal - which items, which icons, who is signed in.
 *
 * Icons are 16px at stroke 1.8 to match the design. lucide's defaults are 24
 * and 2, which reads noticeably heavier beside 12.5px labels.
 */
const NAV: NavItem[] = [
  { to: ROUTES.dashboard, label: 'Monitoring', icon: <Activity size={16} strokeWidth={1.8} /> },
  {
    to: ROUTES.tickets,
    label: 'Support tickets',
    icon: <LifeBuoy size={16} strokeWidth={1.8} />,
  },
  { to: ROUTES.issues, label: 'Issues', icon: <Ticket size={16} strokeWidth={1.8} /> },
  { to: ROUTES.crashes, label: 'Crashes', icon: <Bug size={16} strokeWidth={1.8} /> },
  { to: ROUTES.logs, label: 'Logs', icon: <Terminal size={16} strokeWidth={1.8} /> },
  { to: ROUTES.deployments, label: 'Deployment', icon: <Rocket size={16} strokeWidth={1.8} /> },
  { to: ROUTES.releases, label: 'Releases', icon: <Package size={16} strokeWidth={1.8} /> },
  { to: ROUTES.settings, label: 'Settings', icon: <Settings size={16} strokeWidth={1.8} /> },
];

/**
 * Screen titles for the page header, from the design's own `meta` map.
 *
 * Kept beside the nav because they change together: a screen renamed in one
 * and not the other is how a header and a sidebar start disagreeing.
 */
const TITLES: Record<string, string> = {
  [ROUTES.dashboard]: 'Monitoring Dashboard',
  [ROUTES.tickets]: 'Support tickets',
  [ROUTES.issues]: 'Issues',
  [ROUTES.crashes]: 'Crashes',
  [ROUTES.logs]: 'Logs',
  [ROUTES.deployments]: 'Deployment',
  [ROUTES.versions]: 'Releases',
  [ROUTES.releases]: 'Releases',
  [ROUTES.settings]: 'Production Settings',
};

export function Sidebar({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const user = useAppSelector((state) => state.auth.user);
  const { resolved, setTheme } = useTheme();
  const logout = useLogout();

  return (
    <AppShell
      title={
        TITLES[pathname] ??
        (pathname.startsWith(`${ROUTES.tickets}/`)
          ? 'Support ticket'
          : pathname.startsWith(`${ROUTES.releases}/`)
            ? 'Release'
            : '')
      }
      portalName="Operations"
      storageKey="nudent.sidebar.production"
      logo={<Logo />}
      nav={NAV}
      user={user ? { name: user.name, role: user.role } : undefined}
      renderLink={(item, content, collapsed) => (
        <NavLink
          to={item.to}
          title={collapsed ? item.label : undefined}
          className={({ isActive }) => navItemClass(isActive, collapsed)}
        >
          {content}
        </NavLink>
      )}
      footer={
        <>
          <button
            type="button"
            onClick={() => {
              setTheme(resolved === 'dark' ? 'light' : 'dark');
            }}
            className={navItemClass(false, false)}
          >
            <span className="flex size-4 shrink-0 items-center justify-center">
              {resolved === 'dark' ? (
                <Sun size={16} strokeWidth={1.8} />
              ) : (
                <Moon size={16} strokeWidth={1.8} />
              )}
            </span>
            <span className="truncate">{resolved === 'dark' ? 'Light mode' : 'Dark mode'}</span>
          </button>

          <button
            type="button"
            disabled={logout.isPending}
            onClick={() => {
              logout.mutate();
            }}
            className={navItemClass(false, false)}
          >
            <span className="flex size-4 shrink-0 items-center justify-center">
              <LogOut size={16} strokeWidth={1.8} />
            </span>
            <span className="truncate">Sign out</span>
          </button>
        </>
      }
    >
      {children}
    </AppShell>
  );
}
